import type { Meal } from "@/components/nutrition/mealColumns";
import type { Workout } from "@/components/workouts/workoutsColumns";
import { toJsonDocument } from "./jsonDocument";

export type WorkoutEditorDocument = {
  workout: Workout;
  name: string;
  description: string;
};

export type MealEditorDocument = {
  mealname: string;
  meals: Meal[];
  deletedMeals: string[];
  deletedFoods: { foodId: string; mealId: string }[];
};

export type TrainingTemplateEditorDocument = {
  planName: string;
  workouts: Workout[];
  relativeScheduleItems: {
    id?: string;
    workout_id: string;
    day_offset: number;
    order_index: number;
  }[];
  schedulingMode: "fixed" | "dated";
  deletedWorkouts: string[];
};

export type ClientTrainingEditorDocument = {
  planName: string;
  workouts: Workout[];
  scheduleItems: {
    id: string;
    workout_id: string;
    day_index: number;
    order_index: number;
    rolling_period?: number;
  }[];
  calendarScheduleItems: {
    id: string;
    workout_id: string;
    scheduled_date: string;
    order_index: number;
  }[];
  schedulingMode: "fixed" | "dated";
  deletedWorkouts: string[];
  scheduleViewWeeks: number;
};

const stripWorkout = (workout: Workout): Workout => {
  const { exercises: _exercises, ...rest } = workout as Workout & {
    exercises?: unknown;
  };
  return rest as Workout;
};

export const toWorkoutEditorDocument = (
  input: WorkoutEditorDocument,
): WorkoutEditorDocument =>
  toJsonDocument({
    workout: stripWorkout(input.workout),
    name: input.name,
    description: input.description ?? "",
  });

export const toMealEditorDocument = (
  input: MealEditorDocument,
): MealEditorDocument => toJsonDocument(input);

export const toTrainingTemplateEditorDocument = (
  input: TrainingTemplateEditorDocument,
): TrainingTemplateEditorDocument =>
  toJsonDocument({
    ...input,
    workouts: input.workouts.map(stripWorkout),
  });

export const toClientTrainingEditorDocument = (
  input: ClientTrainingEditorDocument,
): ClientTrainingEditorDocument =>
  toJsonDocument({
    ...input,
    workouts: input.workouts.map(stripWorkout),
    scheduleItems: input.scheduleItems.map((item) => ({
      id: item.id,
      workout_id: item.workout_id,
      day_index: item.day_index,
      order_index: item.order_index,
      rolling_period: item.rolling_period,
    })),
  });

export const hydrateScheduleWorkouts = <
  T extends { workout_id: string; workout?: Workout },
>(
  items: T[],
  workouts: Workout[],
): T[] => {
  const byId = new Map(workouts.map((workout) => [workout.id, workout]));
  return items.map((item) => ({
    ...item,
    workout: byId.get(item.workout_id),
  }));
};
